// src/components/ErrorBoundary.jsx

import React from "react";

/**
 * Catches render errors anywhere below it and shows a recoverable message
 * instead of an empty page.
 *
 * Without this, a single component throwing — a malformed item, a missing
 * field, a bad prop — unmounts the entire React tree and leaves a blank white
 * screen with nothing but a console error. For a visitor standing in a gallery
 * with a phone, a blank page is indistinguishable from a broken site.
 *
 * Must be a class component: React has no hook equivalent of
 * componentDidCatch.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("Uncaught error in component tree:", error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="auction-container error-boundary">
        <h1>Something went wrong</h1>
        <p>
          This page didn't load correctly. Returning to the gallery usually
          fixes it.
        </p>

        <button type="button" className="cta-button" onClick={this.handleReset}>
          Back to the gallery
        </button>

        {process.env.NODE_ENV === "development" && this.state.error && (
          <pre className="error-boundary-detail">
            {this.state.error.toString()}
          </pre>
        )}
      </div>
    );
  }
}
